/**
 * Push notifications (FCM on Android, APNs via FCM on iOS).
 *
 * Native-only: on the web every function is a no-op so the same call sites work
 * in the browser build. Tokens are stored in `public.push_tokens` (RLS: owner
 * only) and refreshed automatically by the plugin's `registration` listener.
 */
import { Capacitor } from '@capacitor/core';
import { PushNotifications, type Token, type ActionPerformed, type PushNotificationSchema } from '@capacitor/push-notifications';
import { supabase } from '@/integrations/supabase/client';
import { reportError } from './monitoring';

type Navigate = (path: string) => void;

const isNative = Capacitor.isNativePlatform();
let listenersBound = false;
let navigateRef: Navigate | null = null;

/** Map a notification payload to an in-app route. */
export function routeForNotification(data: Record<string, unknown> | undefined): string {
  const type = String(data?.type ?? '');
  const entityId = data?.entity_id ? String(data.entity_id) : '';
  switch (type) {
    case 'message':
      return entityId ? `/conversation/${entityId}` : '/messages';
    case 'application':
      return entityId ? `/offers/${entityId}` : '/my-applications';
    case 'follow':
      return entityId ? `/profile/${entityId}` : '/notifications';
    case 'comment':
    case 'like':
      return '/notifications';
    case 'club_invitation':
      return entityId ? `/club/${entityId}` : '/club';
    case 'event':
      return entityId ? `/club/${entityId}` : '/notifications';
    case 'offer':
      return entityId ? `/offers/${entityId}` : '/offers';
    default:
      return '/notifications';
  }
}

async function saveToken(token: string) {
  const { data: auth } = await supabase.auth.getUser();
  const userId = auth.user?.id;
  if (!userId) return;
  const { error } = await supabase.from('push_tokens').upsert(
    {
      user_id: userId,
      token,
      platform: Capacitor.getPlatform(),
      last_seen_at: new Date().toISOString(),
    },
    { onConflict: 'token' },
  );
  if (error) reportError(error, { source: 'push.saveToken' });
}

/**
 * Ask for permission (once) and register the device.
 * Returns the granted state; safe to call on every app start.
 */
export async function registerPush(navigate?: Navigate): Promise<boolean> {
  if (navigate) navigateRef = navigate;
  if (!isNative) return false;
  try {
    let permission = await PushNotifications.checkPermissions();
    if (permission.receive === 'prompt' || permission.receive === 'prompt-with-rationale') {
      permission = await PushNotifications.requestPermissions();
    }
    if (permission.receive !== 'granted') return false;

    bindListeners();
    await PushNotifications.register();
    return true;
  } catch (error) {
    reportError(error, { source: 'push.register' });
    return false;
  }
}

function bindListeners() {
  if (listenersBound) return;
  listenersBound = true;

  // New or refreshed token.
  PushNotifications.addListener('registration', (token: Token) => {
    void saveToken(token.value);
  });

  PushNotifications.addListener('registrationError', (err) => {
    reportError(err, { source: 'push.registrationError' });
  });

  // Foreground delivery — the OS does not display these automatically.
  PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
    window.dispatchEvent(new CustomEvent('nextchamp:push', { detail: notification }));
  });

  // User tapped the notification (background / cold start).
  PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
    const path = routeForNotification(action.notification.data as Record<string, unknown>);
    if (navigateRef) navigateRef(path);
    else window.location.href = path;
  });
}

/** Remove the device token on logout so the user stops receiving pushes. */
export async function unregisterPush() {
  if (!isNative) return;
  try {
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth.user?.id;
    await PushNotifications.removeAllDeliveredNotifications();
    if (userId) {
      await supabase.from('push_tokens').delete().eq('user_id', userId).eq('platform', Capacitor.getPlatform());
    }
  } catch (error) {
    reportError(error, { source: 'push.unregister' });
  }
}
